import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getHealthAlerts } from '../lib/api';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import {
    AlertTriangle, Moon, Droplets, Brain, Dumbbell,
    ChevronRight, X, Lightbulb, CheckCircle2
} from 'lucide-react';

export function HealthAlerts({ onDashboard = false }) {
    const { language } = useLanguage();
    const navigate = useNavigate();
    const [alerts, setAlerts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dismissed, setDismissed] = useState([]);
    const [expandedAlert, setExpandedAlert] = useState(null);

    useEffect(() => {
        loadAlerts();
        // Load dismissed alerts from localStorage
        const saved = localStorage.getItem('dismissed_alerts');
        if (saved) {
            const parsed = JSON.parse(saved);
            // Only keep dismissals from last 24h
            const recent = parsed.filter(d => Date.now() - d.time < 86400000);
            setDismissed(recent.map(d => d.id));
        }
    }, []);

    const loadAlerts = async () => {
        try {
            const response = await getHealthAlerts();
            setAlerts(response.data.alerts || []);
        } catch (error) {
            console.error('Failed to load health alerts:', error);
        } finally {
            setLoading(false);
        }
    };

    const dismissAlert = (alertId) => {
        setDismissed(prev => [...prev, alertId]);
        const saved = JSON.parse(localStorage.getItem('dismissed_alerts') || '[]');
        saved.push({ id: alertId, time: Date.now() });
        localStorage.setItem('dismissed_alerts', JSON.stringify(saved));
    };

    const getIcon = (iconName) => {
        switch (iconName) {
            case 'moon': return Moon;
            case 'droplets': return Droplets;
            case 'brain': return Brain;
            case 'dumbbell': return Dumbbell;
            default: return AlertTriangle;
        }
    };

    const getPriorityStyles = (priority) => {
        switch (priority) {
            case 'high':
                return 'bg-rose-50 dark:bg-rose-950/30 border-rose-200 dark:border-rose-800';
            case 'medium':
                return 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800';
            default:
                return 'bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800';
        }
    };

    const getPriorityIconStyles = (priority) => {
        switch (priority) {
            case 'high':
                return 'bg-rose-100 dark:bg-rose-900 text-rose-600';
            case 'medium':
                return 'bg-amber-100 dark:bg-amber-900 text-amber-600';
            default:
                return 'bg-blue-100 dark:bg-blue-900 text-blue-600';
        }
    };

    const visibleAlerts = alerts.filter(a => !dismissed.includes(a.id));

    if (loading || visibleAlerts.length === 0) {
        return null;
    }

    // On dashboard, show only first alert compact
    if (onDashboard) {
        const alert = visibleAlerts[0];
        const Icon = getIcon(alert.icon);
        const title = language === 'de' ? alert.title_de : alert.title_en;

        return (
            <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`p-3 rounded-xl border ${getPriorityStyles(alert.priority)} cursor-pointer`}
                onClick={() => navigate('/health')}
            >
                <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${getPriorityIconStyles(alert.priority)}`}>
                        <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                            <p className="font-medium text-sm truncate">{title}</p>
                            {visibleAlerts.length > 1 && (
                                <Badge variant="outline" className="text-xs">
                                    +{visibleAlerts.length - 1}
                                </Badge>
                            )}
                        </div>
                        <p className="text-xs text-muted-foreground">
                            {language === 'de' ? 'Tippen für Details' : 'Tap for details'}
                        </p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                </div>
            </motion.div>
        );
    }

    // Full view for Health page
    return (
        <div className="space-y-3">
            <AnimatePresence>
                {visibleAlerts.map((alert, index) => {
                    const Icon = getIcon(alert.icon);
                    const title = language === 'de' ? alert.title_de : alert.title_en;
                    const message = language === 'de' ? alert.message_de : alert.message_en;
                    const tips = language === 'de' ? alert.tips_de : alert.tips_en;
                    const actionLabel = language === 'de' ? alert.action_de : alert.action_en;
                    const isExpanded = expandedAlert === alert.id;

                    return (
                        <motion.div
                            key={alert.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, x: -100 }}
                            transition={{ delay: index * 0.1 }}
                        >
                            <Card className={`overflow-hidden border-2 ${getPriorityStyles(alert.priority)}`}>
                                <CardContent className="p-4">
                                    <div className="flex items-start gap-3">
                                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${getPriorityIconStyles(alert.priority)}`}>
                                            <Icon className="w-6 h-6" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-start justify-between gap-2">
                                                <div>
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <p className="font-semibold">{title}</p>
                                                        <Badge variant={alert.priority === 'high' ? 'destructive' : 'secondary'} className="text-xs">
                                                            {alert.priority === 'high' 
                                                                ? (language === 'de' ? 'Wichtig' : 'Important')
                                                                : (language === 'de' ? 'Hinweis' : 'Notice')}
                                                        </Badge>
                                                    </div>
                                                    <p className="text-sm text-muted-foreground">{message}</p>
                                                </div>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8 flex-shrink-0"
                                                    onClick={() => dismissAlert(alert.id)}
                                                >
                                                    <X className="w-4 h-4" />
                                                </Button>
                                            </div>

                                            {/* Expandable tips section */}
                                            <div className="mt-3">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-8 px-2 text-xs"
                                                    onClick={() => setExpandedAlert(isExpanded ? null : alert.id)}
                                                >
                                                    <Lightbulb className="w-3 h-3 mr-1" />
                                                    {language === 'de' ? 'Tipps anzeigen' : 'Show tips'}
                                                </Button>

                                                <AnimatePresence>
                                                    {isExpanded && (
                                                        <motion.div
                                                            initial={{ height: 0, opacity: 0 }}
                                                            animate={{ height: 'auto', opacity: 1 }}
                                                            exit={{ height: 0, opacity: 0 }}
                                                            className="overflow-hidden"
                                                        >
                                                            <div className="mt-3 p-3 rounded-lg bg-background/50 space-y-2">
                                                                {tips.map((tip, i) => (
                                                                    <div key={i} className="flex items-start gap-2 text-sm">
                                                                        <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                                                                        <span>{tip}</span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </motion.div>
                                                    )}
                                                </AnimatePresence>
                                            </div>

                                            {/* Action button */}
                                            <div className="mt-3">
                                                <Button
                                                    size="sm"
                                                    onClick={() => navigate(alert.action_link)}
                                                    className="gap-1"
                                                >
                                                    {actionLabel}
                                                    <ChevronRight className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </motion.div>
                    );
                })}
            </AnimatePresence>
        </div>
    );
}
